import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getReadingDateList(
    $filter: readingFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getReadingList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        datetime
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.ReadingListState = {
  result: null,
  error: null,
  loading: false
};

export const getReadingDateListRequest = createAsyncThunk<
  TYPES.ReadingList,
  {
    filter?: TYPES.ReadingFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'reading/get_reading_date_list',
  async (
  params: {
    filter?: TYPES.ReadingFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getReadingList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getReadingDateListSlice = createSlice({
  name: 'get_reading_date_list',
  initialState: initialState,
  reducers: {
    getReadingDateListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getReadingDateListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getReadingDateListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getReadingDateListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getReadingDateListClear } =
getReadingDateListSlice.actions;

export const getReadingDateList = createSelector(
  (state: RootState) => state.reading.getReadingDateList.result,
  (state: RootState) => state.reading.getReadingDateList.error,
  (state: RootState) => state.reading.getReadingDateList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getReadingDateListSlice.reducer;